// @ts-nocheck

import React, { FC, useEffect, useState, useRef, useContext } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { QRCodeGenerator } from 'src/shared/components';
import {
  GameHeader,
  ExampleImagesSection,
  TextFieldsSection,
  ImagesSection,
  MusicSection,
  FileDownloadSection,
  ActionsSection,
  ResultsSection,
  processFormData,
  GameExpiredOverlay,
} from 'src/shared/components/Constructor';
import { FormValues, TextFieldConfig, ImageFieldConfig } from 'src/shared/components/Constructor/types';

import InfoIcon from '@mui/icons-material/Info';
import { Container, Box, LinearProgress, Typography, Grid, IconButton, Tooltip, Checkbox, FormControlLabel, Button, Alert, Divider } from '@mui/material';

import { RealProgress } from '@shared/components/Constructor/GameCreationProgress';
import MusicUploader from '@shared/components/MusicUploader';
import WaveformTrimmer from '@shared/components/WaveformTrimmer';
import type { GameKey } from '@shared/types';
import { isGameExpired } from '@shared/utils/gameExpiration';
import { audioBufferToWav } from '@shared/utils/audio-utils';

import { authContext } from '@store/index';

import * as ClientActions from './client.actions';
import { getIsClientFormDisabled } from './client.utils';

import styles from './client.module.scss';

interface ClientPageProps {
  gameKey: GameKey;
}

// Конфигурация полей для ClientCapybara
const textFields: TextFieldConfig[] = [
  { name: 'textTitle', label: 'clientCapybara.textTitle', placeholder: 'clientCapybara.textTitlePlaceholder', tooltip: 'clientCapybara.textTitleTooltip', required: false },
  { name: 'urlButton', label: 'clientCapybara.urlButton', placeholder: 'clientCapybara.urlButtonPlaceholder', tooltip: 'clientCapybara.urlButtonTooltip', required: false },
];

const imageFields: ImageFieldConfig[] = [
  { name: 'imagePlayer', label: 'clientCapybara.imagePlayer', aspectRatio: 1, tooltip: 'clientCapybara.imagePlayerTooltip', required: false },
];

const ClientPageCapybara: FC<ClientPageProps> = ({ gameKey: initialGameKey }) => {
  useEffect(() => {
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
  }, []);

  const { t } = useTranslation();
  const [authData] = useContext(authContext);

  const [gameKey, setGameKey] = useState(initialGameKey);
  const { gameBuild: alreadyExistedGameBuild } = gameKey;
  const [_, setActivateGameDate] = ClientActions.useSetActivateGameDate();
  const gameDefaults = ClientActions.useFetchGameDefaults(gameKey.gameName);
  const [existingGameBuild, fetchGameBuild, gameBuildError, gameBuildLoading] = ClientActions.useFetchGameBuild(
    gameKey.gameBuildId
  );

  const [createdGameBuild, handleGameBuildSubmit, gameBuildCreationLoading] =
    ClientActions.useCreateGameBuild();
  const [trimmedAudioFile, setTrimmedAudioFile] = useState<File | null>(null);
  const [showProgress, setShowProgress] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [stepProgress, setStepProgress] = useState(0);
  const croppedPixelsDataRef = useRef<{ [key: string]: any }>({});
  const [processedImages, setProcessedImages] = useState<{ [key: string]: File }>({});


  const gameBuild = createdGameBuild ?? existingGameBuild ?? alreadyExistedGameBuild;
  const showGameLink = Boolean(gameBuild && gameBuild._id);

  // Проверка истекшей игры
  const [isGameExpiredState, setIsGameExpiredState] = useState(false);

  useEffect(() => {
    // Проверяем, истекла ли игра
    const expired = isGameExpired(gameKey.activateDate, gameKey.gameDayCount);
    setIsGameExpiredState(expired);
  }, [gameKey.activateDate, gameKey.gameDayCount]);

  // Определяем режим только для чтения
  const isReadOnly = authData?.isReadOnly || false;

  const {
    handleSubmit,
    formState: { errors },
    setError,
    control,
    reset,
    getValues,
    setValue,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      language: 'ru-RU',
      textTitle: '',
      imagePlayer: null,
      urlButton: '',
      fileDownloadUrl: '',
      customMusicFile: null,
      waveformTrimmer: null,
    },
  });

  const watchedValues = useWatch({ control });

  // Отслеживаем изменения в поле imagePlayer и очищаем обработанные изображения
  useEffect(() => {
    const imagePlayerValue = watchedValues.imagePlayer;
    if (imagePlayerValue && processedImages.imagePlayer) {
      // Если пользователь загрузил новое изображение через обычную форму, очищаем обработанные
      setProcessedImages(prev => {
        const newState = { ...prev };
        delete newState.imagePlayer;
        return newState;
      });
      console.log('Новое изображение загружено через форму, обработанные изображения очищены');
    }
  }, [watchedValues.imagePlayer, processedImages.imagePlayer]);

  // Загружаем существующие данные игры
  useEffect(() => {
    // console.log('🔍 Loading game data:', {
    //   gameBuild,
    //   gameDefaults,
    //   existingGameBuild,
    //   createdGameBuild
    // });

    if (gameBuild) {
      console.log('📦 GameBuild data:', gameBuild);
      const newValues: FormValues = {};
      
      // Заполняем текстовые поля
      textFields.forEach(field => {
        const keyMap: Record<string, keyof typeof gameBuild> = {
          textTitle: 'textTitle',
          urlButton: 'urlButton',
        };
        const buildKey = keyMap[field.name];
        if (buildKey) {
          newValues[field.name] = gameBuild[buildKey] || '';
        }
      });
      
      // Заполняем поля изображений
      imageFields.forEach(field => {
        const keyMap: Record<string, keyof typeof gameBuild> = {
          imagePlayer: 'imagePlayer',
        };
        const buildKey = keyMap[field.name];
        if (buildKey) {
          newValues[field.name] = gameBuild[buildKey] || '';
        }
      });
      
      // Дополнительные поля
      newValues.customMusicFile = gameBuild.musicUrl || gameDefaults?.defaultMusicUrl || '';
      // Разделяем: urlButton для URL, fileDownloadUrl для файла
      // Используем пустые строки вместо null для избежания предупреждений React
      newValues.urlButton = gameBuild.urlButton || '';
      newValues.fileDownloadUrl = (gameBuild as any).fileDownloadUrl || '';
      newValues.waveformTrimmer = null;
      
      console.log('🎵 Music URL:', {
        gameBuildMusicUrl: gameBuild.musicUrl,
        gameDefaultsMusicUrl: gameDefaults?.defaultMusicUrl,
        finalMusicUrl: newValues.customMusicFile
      });
      
      reset(newValues);
    } else if (gameDefaults) {
      // console.log('🎵 Using default music:', gameDefaults.defaultMusicUrl);
      reset({
        language: 'ru-RU',
        textTitle: '',
        imagePlayer: null,
        urlButton: '',
        fileDownloadUrl: '',
        customMusicFile: gameDefaults.defaultMusicUrl || '',
        waveformTrimmer: null,
      });
    } else {
      console.log('🎵 Using fallback values (no gameDefaults)');
      reset({
        language: 'ru-RU',
        textTitle: '',
        imagePlayer: null,
        urlButton: '',
        fileDownloadUrl: '',
        customMusicFile: '',
        waveformTrimmer: null,
      });
    }
  }, [gameBuild, reset, gameDefaults, existingGameBuild?._id]);

  // Обновляем gameKey после успешного создания игры
  useEffect(() => {
    if (createdGameBuild) {
      const result = createdGameBuild;
      if (result && result._id && result._id !== gameKey.gameBuildId) {
        setGameKey((prev) => ({
          ...prev,
          gameBuild: result,
          gameBuildId: result._id,
        }));
      }

      if (!gameKey.activateDate) {
        setActivateGameDate(gameKey._id);
        setGameKey((prev) => ({
          ...prev,
          activateDate: new Date(),
        }));
      }
    }
  }, [createdGameBuild, setActivateGameDate, gameKey._id, gameKey.activateDate, gameKey.gameBuildId]);

  const onSubmit = async (data: FormValues) => {
    try {
      setShowProgress(true);
      setCurrentStep(t('constructor.processingForm'));
      setStepProgress(10);

      // Автоматическая обрезка и компрессия музыки, если она новая
      let audioFileToUse = trimmedAudioFile || data.customMusicFile;
      
      // Если есть музыкальный файл и он новый (не обрезанный), запускаем автоматическую обрезку
      if (data.customMusicFile instanceof File && !trimmedAudioFile) {
        setCurrentStep(t('constructor.music'));
        setStepProgress(20);
        console.log('Запускаем автоматическую обрезку и компрессию музыки...');
        
        try {
          // Создаем AudioContext для обрезки
          const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
          
          // Читаем файл
          const arrayBuffer = await data.customMusicFile.arrayBuffer();
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          
          console.log('Аудио декодировано, длительность:', audioBuffer.duration, 'сек');
          
          // Используем полную длительность аудио без автоматической обрезки
          const startTime = 0;
          const endTime = audioBuffer.duration;
          
          console.log('Используем полную длительность аудио:', { startTime, endTime, duration: audioBuffer.duration });
          
          // Вычисляем сэмплы для обрезки
          const sampleRate = audioBuffer.sampleRate;
          const startSample = Math.floor(startTime * sampleRate);
          const endSample = Math.floor(endTime * sampleRate);
          const trimmedLength = endSample - startSample;
          
          // Создаем новый AudioBuffer для обрезанного аудио
          const trimmedBuffer = audioContext.createBuffer(
            audioBuffer.numberOfChannels,
            trimmedLength,
            sampleRate
          );
          
          // Копируем данные из выбранного диапазона
          for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
            const originalData = audioBuffer.getChannelData(channel);
            const trimmedData = trimmedBuffer.getChannelData(channel);
            
            for (let i = 0; i < trimmedLength; i++) {
              trimmedData[i] = originalData[startSample + i];
            }
          }
          
          // Конвертируем AudioBuffer в MP3 используя MP3Compressor
          const { MP3Compressor } = await import('@shared/utils/mp3-compression');
          const compressor = new MP3Compressor();
          
          // Сохраняем оригинальное название файла
          const originalFileName = data.customMusicFile.name;
          const fileNameWithoutExt = originalFileName.replace(/\.[^/.]+$/, '');
          
          // Создаем WAV файл из AudioBuffer
          const wavBlob = await audioBufferToWav(trimmedBuffer);
          const tempFile = new File([wavBlob], `${fileNameWithoutExt}`, { type: 'audio/wav' });
          
          const compressionResult = await compressor.compressMP3(tempFile);
          const trimmedFile = compressionResult.compressedFile;
          
          console.log('Автоматическая обрезка завершена:', trimmedFile.name, 'размер:', trimmedFile.size);
          
          audioFileToUse = trimmedFile;
          // Проверяем состояние AudioContext перед закрытием
          if (audioContext.state !== 'closed') {
            try {
              audioContext.close();
            } catch (error) {
              console.error('Ошибка при закрытии AudioContext:', error);
            }
          }
          
        } catch (error) {
          console.error('Ошибка автоматической обрезки музыки:', error);
          // Если обрезка не удалась, используем оригинальный файл
          audioFileToUse = data.customMusicFile;
        }
      }

      setCurrentStep(t('constructor.images'));
      setStepProgress(40);

      // Создаем модифицированные данные с обработанными изображениями
      const modifiedData = { ...data, customMusicFile: audioFileToUse };
      
      // Заменяем оригинальные изображения на обработанные, если они есть
      if (processedImages.imagePlayer) {
        modifiedData.imagePlayer = processedImages.imagePlayer;
      }

      const formData = await processFormData(
        modifiedData,
        gameKey,
        trimmedAudioFile,
        {
          imageFields: imageFields.map(f => f.name),
          musicFields: ['customMusicFile'],
          fileFields: ['urlButton'],
          gameType: 'capybara',
        }
      );

      setCurrentStep(t('constructor.upload'));
      setStepProgress(60);

      await handleGameBuildSubmit(formData);

      setCurrentStep(t('constructor.creation'));
      setStepProgress(80);

      // Имитируем завершение создания
      setTimeout(() => {
        setCurrentStep(t('constructor.complete'));
        setStepProgress(100);
      }, 1000);

      // Скрываем прогресс после завершения
      setTimeout(() => {
        setShowProgress(false);
        setCurrentStep('');
        setStepProgress(0);
      }, 2000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setError('root', { message: 'Ошибка при сохранении игры' });
      setShowProgress(false);
      setCurrentStep('');
      setStepProgress(0);
    }
  };

  const onError = (errors: any) => {
    const missingFields = [
      ...textFields.filter(f => f.required && errors[f.name]),
      ...imageFields.filter(f => f.required && errors[f.name]),
    ];

    if (missingFields.length > 0) {
      const fieldsList = missingFields.map(({ label }) => `📝 ${t(label)}`).join('\n');
      const errorMessage = `${t('client.validation.requiredFieldsMessage')}\n\n${fieldsList}`;
      setError('root', { 
        message: errorMessage
      });
    }
  };



  const isFormDisabled = getIsClientFormDisabled(
    gameBuildCreationLoading,
    gameBuildLoading,
    isReadOnly,
    isGameExpiredState
  );

  if (isGameExpiredState) {
    return <GameExpiredOverlay isVisible={true} />;
  }

  if (!gameDefaults) {
    console.log('⚠️ Game defaults not loaded, using fallback values');
    // Используем fallback значения вместо ожидания загрузки
  }

  return (
    <Container maxWidth="lg" className={styles.gameContainer}>
      <GameHeader 
        gameDefaults={gameDefaults}
        activateDate={gameKey.activateDate ? (typeof gameKey.activateDate === 'string' ? new Date(gameKey.activateDate) : gameKey.activateDate) : null}
        editDayCount={gameKey.editDayCount}
        gameDayCount={gameKey.gameDayCount}
      />

      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ maxWidth: '800px', width: '100%' }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextFieldsSection
                  control={control}
                  errors={errors}
                  fields={textFields}
                />



                <ImagesSection
                  control={control}
                  errors={errors}
                  isReadOnly={isReadOnly}
                  cropDataRef={croppedPixelsDataRef}
                  images={imageFields}
                  gameType="capybara"

                />

                <MusicSection
                  control={control}
                  errors={errors}
                  isReadOnly={isReadOnly}
                  setError={setError}
                  onTrimmedFileChange={setTrimmedAudioFile}
                  musicFieldName="customMusicFile"
                  waveformTrimmerFieldName="waveformTrimmer"
                />

                <FileDownloadSection
                  control={control}
                  errors={errors}
                  isReadOnly={isReadOnly}
                  setError={setError}
                  setValue={setValue}
                  getValues={getValues}
                  fieldName="urlButton"
                />
              </Grid>
            </Grid>
            
            {/* Кнопки и результаты снизу */}
            <Box sx={{ mt: 3 }}>
              <ActionsSection
                gameBuild={gameBuild}
                gameBuildCreationLoading={gameBuildCreationLoading}
                isReadOnly={isReadOnly}
                onTestGameClick={() => {
                  if (gameBuild?._id) {
                    window.open(`/game?buildId=${gameBuild._id}&isTesting=true`, '_blank');
                  }
                }}
                hasErrors={Object.keys(errors).length > 0}
              />
              
              {/* Прогресс создания игры */}
              {showProgress && (
                <Box className={styles.progressContainer} sx={{ mt: 2 }}>
                  <Typography variant="h6" className={styles.progressTitle}>
                    {currentStep}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={stepProgress} 
                    className={styles.progressBar}
                  />
                </Box>
              )}
              
              {showGameLink && gameBuild && (
                <ResultsSection 
                  errors={errors}
                  gameBuild={gameBuild} 
                  showGameLink={showGameLink}
                />
              )}

              {/* QR-код компонент */}
              <QRCodeGenerator
                gameUrl={gameBuild ? `${window.location.origin}/game?buildId=${gameBuild._id}` : ''}
                gameBuildId={gameBuild?._id}
                showGameLink={showGameLink}
              />
            </Box>
          </Box>
        </Box>
      </form>
    </Container>
  );
};

export default ClientPageCapybara;
